// src/pages/PanelFirma/FirmaCargaMasiva.js
//
// Tab dentro del Panel de Firma para firmar Cartas Poder del módulo Carga Masiva.
// Usa endpoints genéricos /panel-firma filtrando por tipo=CM_CARTA_PODER.
// Firma batch con token USB (servicio local en 127.0.0.1:5850).

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Paper, Typography, IconButton, Button, Tooltip, Chip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox,
    CircularProgress, LinearProgress, Alert,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    VpnKey as TokenIcon,
    CheckCircle as CheckCircleIcon,
    Description as DescriptionIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
    panelFirmaPendientesJWT,
    panelFirmaDescargarPdfJWT,
    panelFirmaSubirPdfFirmadoJWT,
} from '../../api/endpoints';
import PdfPreviewDialog from '../../components/CargaMasiva/PdfPreviewDialog';
import useServicioFirma, { fetchFirmaLocal } from '../../hooks/useServicioFirma';

const COLOR = '#37474f';

export default function FirmaCargaMasiva() {
    const { enqueueSnackbar } = useSnackbar();
    const servicio = useServicioFirma();
    const [pendientes, setPendientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [seleccionadas, setSeleccionadas] = useState(new Set());
    const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');
    const [dialogPin, setDialogPin] = useState(false);
    const [pin, setPin] = useState('');
    const [firmando, setFirmando] = useState(false);
    const [progreso, setProgreso] = useState(null);
    const [previewState, setPreviewState] = useState({ open: false, blob: null, titulo: '' });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await panelFirmaPendientesJWT('CM_CARTA_PODER', filtroEstado || undefined);
            if (r?.ok) setPendientes(r.items || r.pendientes || []);
        } catch {
            enqueueSnackbar('Error al cargar pendientes', { variant: 'error' });
        } finally { setLoading(false); }
    }, [filtroEstado, enqueueSnackbar]);

    useEffect(() => { cargar(); }, [cargar]);

    const toggleSeleccion = (id) => {
        setSeleccionadas(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    const toggleTodas = () => {
        const firmables = pendientes.filter(p => p.Estado === 'PENDIENTE' && p.TienePDF);
        if (seleccionadas.size === firmables.length) setSeleccionadas(new Set());
        else setSeleccionadas(new Set(firmables.map(p => p.Id)));
    };

    const verPDF = async (doc) => {
        try {
            const blob = await panelFirmaDescargarPdfJWT(doc.Id, doc.Estado_Firma === 2);
            setPreviewState({
                open: true, blob,
                titulo: `Carta Poder ${doc.Numero || ''} — ${doc.Descripcion || ''}`,
            });
        } catch {
            enqueueSnackbar('Error al cargar PDF', { variant: 'error' });
        }
    };

    const firmarSeleccionadas = async () => {
        if (!pin || seleccionadas.size === 0) return;
        setFirmando(true);
        setProgreso({ done: 0, total: seleccionadas.size, errores: [] });
        const ids = Array.from(seleccionadas);
        let done = 0;
        let corte = null;
        const errores = [];

        for (const id of ids) {
            try {
                // 1. Descargar PDF sin firmar
                const blob = await panelFirmaDescargarPdfJWT(id, false);
                // 2. Firmar con servicio local
                const res = await fetchFirmaLocal(`/firmar?pin=${encodeURIComponent(pin)}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/pdf' },
                    body: blob,
                });
                if (!res.ok) {
                    const text = await res.text();
                    let errObj;
                    try { errObj = JSON.parse(text); } catch { errObj = { error: text }; }
                    throw new Error(errObj.error || 'Error del token');
                }
                const firmado = await res.blob();
                // 3. Subir firmado
                const r = await panelFirmaSubirPdfFirmadoJWT(id, new File([firmado], 'firmado.pdf', { type: 'application/pdf' }));
                if (!r?.ok) throw new Error(r?.mensaje || 'Error al subir firmado');
                done++;
            } catch (err) {
                errores.push({ id, error: err.message || String(err) });
                // Si es error de token o el cliente se cerró, cortar todo
                if (err.cortar || err.message?.includes('token') || err.message?.includes('PIN')) {
                    corte = err.message;
                    break;
                }
            }
            setProgreso({ done: done + errores.length, total: ids.length, errores });
        }

        setFirmando(false);
        setDialogPin(false);
        setPin('');
        setSeleccionadas(new Set());
        enqueueSnackbar(
            corte
                ? `Firmadas: ${done} / ${ids.length}. Se cortó: ${corte}`
                : `Firmadas: ${done} / ${ids.length}. Errores: ${errores.length}`,
            { variant: errores.length > 0 ? 'warning' : 'success' }
        );
        servicio.verificar();
        cargar();
    };

    const firmables = pendientes.filter(p => p.Estado === 'PENDIENTE' && p.TienePDF);

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2, borderRadius: '4px' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <DescriptionIcon sx={{ color: COLOR }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR }}>Cartas Poder — Carga Masiva</Typography>
                    <Box sx={{ flex: 1 }} />
                    {['PENDIENTE', 'FIRMADO', 'RECHAZADO'].map(e => (
                        <Chip key={e} label={e} size="small"
                            variant={filtroEstado === e ? 'filled' : 'outlined'}
                            sx={filtroEstado === e ? { bgcolor: COLOR, color: 'white' } : {}}
                            onClick={() => { setFiltroEstado(e); setSeleccionadas(new Set()); }}
                        />
                    ))}
                    <Tooltip title="Refrescar"><IconButton size="small" onClick={cargar}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </Box>

                {!servicio.listo && filtroEstado === 'PENDIENTE' && (
                    <Alert severity={servicio.disponible === null ? 'info' : 'warning'} sx={{ mt: 2, borderRadius: '4px' }}>
                        {servicio.mensaje}
                    </Alert>
                )}

                {seleccionadas.size > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={`${seleccionadas.size} seleccionadas`} sx={{ bgcolor: COLOR, color: 'white' }} />
                        <Tooltip title={servicio.listo ? '' : servicio.mensaje}>
                            <span>
                                <Button
                                    variant="contained" startIcon={<TokenIcon />}
                                    onClick={() => setDialogPin(true)} disabled={!servicio.listo}
                                    sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' }, textTransform: 'none', fontWeight: 600 }}>
                                    Firmar {seleccionadas.size} con token
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: COLOR }} /></Box>
            ) : pendientes.length === 0 ? (
                <Alert severity="info">No hay cartas poder de Carga Masiva en estado {filtroEstado}.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
                                <TableCell padding="checkbox">
                                    {filtroEstado === 'PENDIENTE' && (
                                        <Checkbox size="small"
                                            checked={firmables.length > 0 && seleccionadas.size === firmables.length}
                                            indeterminate={seleccionadas.size > 0 && seleccionadas.size < firmables.length}
                                            onChange={toggleTodas}
                                        />
                                    )}
                                </TableCell>
                                <TableCell>Número</TableCell>
                                <TableCell>Abogado</TableCell>
                                <TableCell>Descripción</TableCell>
                                <TableCell>Fecha solicitud</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pendientes.map(p => (
                                <TableRow key={p.Id} hover selected={seleccionadas.has(p.Id)}>
                                    <TableCell padding="checkbox">
                                        {p.Estado === 'PENDIENTE' && p.TienePDF && (
                                            <Checkbox size="small" checked={seleccionadas.has(p.Id)} onChange={() => toggleSeleccion(p.Id)} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.Numero || '—'}</TableCell>
                                    <TableCell>{p.Abogado_Nombre || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem' }}>{p.Descripcion || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem' }}>{p.Fecha_Solicitud ? new Date(p.Fecha_Solicitud).toLocaleString('es-AR') : '—'}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={p.Estado}
                                            color={p.Estado === 'FIRMADO' ? 'success' : p.Estado === 'RECHAZADO' ? 'error' : 'warning'}
                                        />
                                        {p.Estado === 'FIRMADO' && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 0.5, verticalAlign: 'middle' }} />}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Ver PDF">
                                            <IconButton size="small" onClick={() => verPDF(p)} disabled={!p.TienePDF}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog PIN */}
            <Dialog open={dialogPin} onClose={() => !firmando && setDialogPin(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ color: COLOR }}>Firmar {seleccionadas.size} cartas poder</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Insertá el PIN del token USB. Se firmarán las <strong>{seleccionadas.size}</strong> cartas seleccionadas en batch.
                    </Typography>
                    <TextField
                        type="password" label="PIN del token" autoFocus fullWidth
                        value={pin} onChange={e => setPin(e.target.value)} disabled={firmando}
                    />
                    {progreso && firmando && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption">Firmando {progreso.done} / {progreso.total}...</Typography>
                            <LinearProgress variant="determinate" value={(progreso.done / progreso.total) * 100} sx={{ mt: 1 }} />
                        </Box>
                    )}
                    {!servicio.listo && !firmando && (
                        <Alert severity="warning" sx={{ mt: 2, borderRadius: '4px' }}>{servicio.mensaje}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogPin(false)} disabled={firmando}>Cancelar</Button>
                    <Button onClick={firmarSeleccionadas} variant="contained" disabled={!pin || firmando || !servicio.listo}
                        sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }}>
                        {firmando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Firmar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <PdfPreviewDialog
                open={previewState.open}
                blob={previewState.blob}
                titulo={previewState.titulo}
                onClose={() => setPreviewState({ open: false, blob: null, titulo: '' })}
            />
        </Box>
    );
}
